import { apiRequest } from './httpRequest';
import type { ChemMoleculeV1Document } from '@peruselab/structure-editor/domain/moleculeProjection';

/** ADR 0020 D2: how sure the check is that something is wrong. */
export type StructureIssueSeverity = 'ERROR' | 'WARNING' | 'INFO';

/** ADR 0020 D6 (#1101): a one-click fix its rule offers, with a translatable label. */
export interface StructureFix {
  readonly kind: 'flip-sign' | 'clear-charge' | 'split-into-ions' | string;
  readonly messageKey: string;
  readonly args: Readonly<Record<string, string>>;
  readonly atomIds: readonly string[];
  readonly bondIds: readonly string[];
}

/** One finding of the server's structure check, on the drawn atom and bond ids. */
export interface StructureIssue {
  readonly rule: string;
  readonly severity: StructureIssueSeverity;
  /** An i18n key under `structureCheck.*` (#897), with its arguments. */
  readonly messageKey: string;
  readonly args: Readonly<Record<string, string>>;
  readonly atomIds: readonly string[];
  readonly bondIds: readonly string[];
  readonly fixes?: readonly StructureFix[];
  /** D5: the author confirmed this warning intended -- by the caller (`byYou`) or not. */
  readonly confirmation?: { readonly byYou: boolean; readonly at: string } | null;
  /** D5: the caller is the structure's author, so may confirm it (or withdraw). */
  readonly canConfirm?: boolean;
}

export interface StructureCheckResult {
  /** Errors first, then warnings, then info. */
  readonly issues: readonly StructureIssue[];
  readonly provenance: { readonly engine: string; readonly engineVersion: string; readonly rulesVersion: string };
  readonly durationMicros: number;
}

// ADR 0020 D1: the one authority on whether a structure is sound as drawn. Nothing is stored and
// nothing leaves PeruseLab; signed in only (#1090).
export async function checkStructure(
  molecule: ChemMoleculeV1Document,
  signal?: AbortSignal
): Promise<StructureCheckResult> {
  return apiRequest<StructureCheckResult>('/chemistry/structure-checks', 'structure check', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(molecule),
    ...(signal ? { signal } : {})
  });
}

/** D5 (#1101): the warning to confirm or withdraw -- the paper it's on, its rule and arguments, an atom. */
export interface StructureConfirmationRequest {
  readonly molecule: ChemMoleculeV1Document;
  readonly rule: string;
  readonly args: Readonly<Record<string, string>>;
  readonly atomId: string;
}

/** The structure's author says the warning is intended; it then holds for everyone (owner, Q1 (a)). */
export async function confirmStructureWarning(request: StructureConfirmationRequest): Promise<void> {
  await apiRequest<void>('/chemistry/structure-checks/confirmations', 'structure check', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request)
  });
}

export async function withdrawStructureConfirmation(request: StructureConfirmationRequest): Promise<void> {
  await apiRequest<void>('/chemistry/structure-checks/confirmations', 'structure check', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request)
  });
}
